import { useEffect, useMemo, useState } from 'react';
import { X, Save, Loader2, ChevronDown, CalendarDays, FileText, Hash, Link2, List, Package2, Truck, UserCircle2, WalletCards, AlignLeft, Download, Phone, MessageCircle, ClipboardList, MessageSquare, History } from 'lucide-react';
import { settingsService } from '../services/settingsService';
import { documentService } from '../services/documentService';
import { driverService } from '../services/driverService';
import { uiMetadataService } from '../services/uiMetadataService';
import { SUPPLY_METADATA_UPDATED_EVENT } from '../utils/metadataEvents';
import { MultiVehicleTypeSelect } from './MultiVehicleTypeSelect';
import { normalizeVehicleType, parseVehicleTypes, formatVehicleTypes } from '../constants/vehicleTypes';

interface SupplyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void> | void;
  editingItem?: any;
  initialData?: any;
}

const FORM_TABS = [
  { key: 'general', label: 'Genel Bilgiler' },
  { key: 'assignment', label: 'Araç & Sürücü' },
  { key: 'pricing', label: 'Fiyat Bilgileri' },
  { key: 'yukleme', label: 'Yükleme Belgeleri' },
  { key: 'bosaltma', label: 'Boşaltma Belgeleri' },
  { key: 'documents', label: 'Fatura & Teslim Belgesi' },
  { key: 'activity', label: 'Eksik sekme' },
] as const;

const FALLBACK_TAB_FIELDS: Record<string, string[]> = {
  general: ['faturaBasligi', 'siparisKodu', 'yuklemeTalepTarihi', 'yuklemeMusterisiAd', 'yuklemeIl', 'yuklemeIlce', 'teslimMusterisiAd', 'teslimIl', 'teslimIlce', 'stopCount', 'vehicleCount', 'palletCount', 'yukAgirlik', 'aracTuru', 'surucuBilgileri', 'durum', 'yukTipi', 'aciklama', 'navlun', 'talepOlusturan'],
  assignment: ['faturaBasligi', 'surucuBilgileri', 'operasyonTakipYuklenecekVardiya', 'aracAtamaTarihi', 'surucuAd', 'tcKimlikNo', 'telefon', 'seferKm', 'navlun', 'komisyon', 'ugarmaBedeli', 'beklemeBedeli', 'gerceklesenTlKm', 'genelToplam', 'aciklama'],
  pricing: ['stopCount', 'yuklemeIl', 'yuklemeIlce', 'teslimIl', 'teslimIlce', 'seferKm', 'navlun', 'genelToplam', 'gerceklesenTlKm', 'ugarmaBedeli', 'yukTipi', 'komisyon', 'ozelYukBelgesi', 'beklemeBedeli', 'irsaliyeFaturaNotu', 'faturaNumarasi', 'islemDurum', 'fiyatAciklama', 'eskalasyonNotu'],
  yukleme: ['yuklemeIrslaliyeNo', 'yuklemeTutanakNo', 'yuklemeBelge1', 'yuklemeBelge2', 'yuklemeBelge3'],
  bosaltma: ['bosaltmaTeslimBelgeNo', 'bosaltmaTutanakNo', 'bosaltmaBelge1', 'bosaltmaBelge2', 'bosaltmaBelge3'],
  documents: ['islemDurum', 'irsaliyeFaturaNotu', 'navlun', 'faturaNumarasi', 'faturaNotlari', 'vkn', 'faturaTutari', 'faturaTarihi', 'onOnayBelgesiNotu', 'tedarikciFirmaUnvani', 'muhasebeIletilmeTarihi', 'teslimBelgesiYuklemeTarihi', 'aracAtamasiGerceklestiren'],
  activity: [],
};

const parseVehicleTypesFromForm = (value: any): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return parseVehicleTypes(String(value));
};

const STATUS_OPTIONS = [
  { value: 'tedarik-bekleniyor', label: 'Tedarik Bekliyor', className: 'bg-pink-500 text-white' },
  { value: 'filo-tedarik-edildi', label: 'Filo Tedarik Edildi', className: 'bg-blue-500 text-white' },
  { value: 'spot-tedarik-edildi', label: 'Spot Tedarik Edildi', className: 'bg-sky-400 text-white' },
  { value: 'teklif-topla', label: 'Teklif Topla', className: 'bg-violet-600 text-white' },
  { value: 'faturalandirildi', label: 'Faturalandırıldı', className: 'bg-emerald-500 text-white' },
  { value: 'spot-teklif-bekleniyor', label: 'Spot Teklif Bekleniyor', className: 'bg-amber-500 text-white' },
  { value: 'iptal', label: 'İptal', className: 'bg-slate-500 text-white' },
] as const;

const PROCESS_STATUS_OPTIONS = [
  { value: 'islem-bekliyor', label: 'İŞLEM BEKLİYOR', className: 'bg-amber-400 text-white' },
  { value: 'islem-tamamlandi', label: 'İŞLEM TAMAMLANDI', className: 'bg-emerald-500 text-white' },
  { value: 'islem-iptal', label: 'İŞLEM İPTAL', className: 'bg-slate-500 text-white' },
] as const;

const viteEnv = ((import.meta as any)?.env || {}) as Record<string, string | undefined>;

const toDateTimeLocal = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const localDate = new Date(date.getTime() + 3 * 60 * 60 * 1000);
  return localDate.toISOString().slice(0, 16);
};

function WorkiomFieldRow({
  icon,
  label,
  required = false,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[280px_minmax(0,1fr)] gap-6 items-center">
      <div className="flex items-center gap-3 text-[15px] font-semibold text-slate-700">
        <span className="text-slate-500">{icon}</span>
        <span>{label}{required ? '*' : ''}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function WorkiomInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-[15px] text-slate-800 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none ${props.className || ''}`} />;
}

function WorkiomTextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-[15px] text-slate-800 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none ${props.className || ''}`} />;
}

function WorkiomLinkedPreview({
  columns,
}: {
  columns: Array<{ label: string; value: string | number | undefined | null }>;
}) {
  const filled = columns.filter((column) => String(column.value ?? '').trim() !== '');
  if (filled.length === 0) return null;

  return (
    <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="grid grid-cols-1 gap-px bg-slate-200 md:grid-cols-2 xl:grid-cols-4">
        {filled.map((column) => (
          <div key={column.label} className="bg-white px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{column.label}</div>
            <div className="mt-1 text-[15px] text-slate-900">{column.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkiomImagePreview({ documents }: { documents: Array<{ id: string; url: string; name: string }> }) {
  if (!documents.length) return null;

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">Yüklenen Belgeler</div>
        <button
          type="button"
          onClick={() => documents.forEach((doc) => window.open(doc.url, '_blank'))}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          <Download className="h-4 w-4" />
          Hepsini indir
        </button>
      </div>

      <div className="flex flex-wrap gap-4">
        {documents.map((doc) => (
          <button key={doc.id} type="button" onClick={() => window.open(doc.url, '_blank')} className="w-28 text-left">
            <div className="h-28 overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
              <img src={doc.url} alt={doc.name} className="h-full w-full object-cover" />
            </div>
            <div className="mt-2 truncate text-sm text-slate-700">{doc.name}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function DocumentUploadPanel({
  documents,
  onUpload,
  onPreview,
  loading,
  open,
  onToggle,
  title,
}: {
  documents: Array<{ id: string; url: string; name: string }>;
  onUpload: (files: FileList | null) => void;
  onPreview: (index: number) => void;
  loading?: boolean;
  open: boolean;
  onToggle: () => void;
  title: string;
}) {
  const firstDocument = documents[0];

  return (
    <div className="relative mt-3">
      <button
        type="button"
        onClick={onToggle}
        className={`flex h-11 w-full items-center gap-3 rounded-lg border px-2 py-1.5 text-left ${open ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-200'} bg-white`}
      >
        {firstDocument ? (
          <div className="h-7 w-7 overflow-hidden rounded border border-slate-200 bg-slate-100">
            <img src={firstDocument.url} alt={firstDocument.name} className="h-full w-full object-cover" />
          </div>
        ) : (
          <div className="flex h-7 w-7 items-center justify-center rounded border border-dashed border-slate-300 text-slate-400">+</div>
        )}
        <div className="min-w-0 flex-1 text-sm text-slate-600">
          {firstDocument ? firstDocument.name : `${title} için yüklemek üzere tıklayın`}
        </div>
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-20 w-[320px] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
          <div className="flex flex-wrap gap-4">
            <label className="flex h-36 w-32 cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 text-center text-slate-600 hover:bg-slate-100">
              <span className="text-3xl leading-none">+</span>
              <span className="mt-2 text-sm font-semibold">Yüklemek için tıklayın veya sürükleyip bırakın</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  onUpload(e.target.files);
                  e.currentTarget.value = '';
                }}
              />
            </label>

            {documents.map((doc, index) => (
              <button key={doc.id} type="button" onClick={() => onPreview(index)} className="w-32 text-left">
                <div className="h-36 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                  <img src={doc.url} alt={doc.name} className="h-full w-full object-cover" />
                </div>
                <div className="mt-2 truncate text-sm text-slate-700">{doc.name}</div>
              </button>
            ))}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              disabled={loading || documents.length === 0}
              onClick={() => documents.forEach((doc) => window.open(doc.url, '_blank'))}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              Hepsini indir
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SupplyFormModal({ isOpen, onClose, onSave, editingItem, initialData }: SupplyFormModalProps) {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<(typeof FORM_TABS)[number]['key']>('general');
  const [metadataTabs, setMetadataTabs] = useState<Array<{ key: string; label: string; fields: Array<{ key: string; label?: string }> }>>([]);
  const [activityTab, setActivityTab] = useState<'all' | 'comments' | 'history'>('all');
  const [unitStopFee, setUnitStopFee] = useState(0);
  const [uploadingDocType, setUploadingDocType] = useState<'irsaliye' | 'fatura' | 'yuklemeBelge1' | 'yuklemeBelge2' | 'yuklemeBelge3' | 'bosaltmaBelge1' | 'bosaltmaBelge2' | 'bosaltmaBelge3' | null>(null);
  const [openDocumentPopover, setOpenDocumentPopover] = useState<'irsaliye' | 'fatura' | 'yuklemeBelge1' | 'yuklemeBelge2' | 'yuklemeBelge3' | 'bosaltmaBelge1' | 'bosaltmaBelge2' | 'bosaltmaBelge3' | null>(null);
  const [driverPickerOpen, setDriverPickerOpen] = useState(false);
  const [driverOptions, setDriverOptions] = useState<any[]>([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [linkedPicker, setLinkedPicker] = useState<{ fieldKey: string; moduleKey: string; open: boolean }>({ fieldKey: '', moduleKey: '', open: false });
  const [linkedSearch, setLinkedSearch] = useState('');
  const [linkedResults, setLinkedResults] = useState<any[]>([]);
  const [documentPreviewModal, setDocumentPreviewModal] = useState<{ title: string; documents: Array<{ id: string; url: string; name: string }>; index: number } | null>(null);
  const [formData, setFormData] = useState<any>({
    movementType: 'warehouse-to-customer',
    durum: 'tedarik-bekleniyor',
    aracTipi: 'Filo',
  });

  useEffect(() => {
    if (!isOpen) return;

    setActiveTab('general');
    setActivityTab('all');
    setOpenDocumentPopover(null);

    if (editingItem) {
      setFormData({
        ...editingItem,
        aracTuru: formatVehicleTypes(parseVehicleTypesFromForm(editingItem.aracTuru || editingItem.vehicle_spec_type || editingItem.arac_turu)),
        requireReferenceWaybill: Boolean(editingItem.requireReferenceWaybill || editingItem.require_reference_waybill),
        phoneOnlyQuote: Boolean(editingItem.phoneOnlyQuote || editingItem.phone_only_quote),
        yuklemeTalepTarihi: toDateTimeLocal(editingItem.yuklemeTalepTarihi),
        istenenTeslimTarihi: toDateTimeLocal(editingItem.istenenTeslimTarihi),
      });
      return;
    }

    if (initialData) {
      setFormData({
        movementType: 'warehouse-to-customer',
        durum: 'tedarik-bekleniyor',
        aracTipi: 'Filo',
        ...initialData,
      });
      return;
    }

    setFormData({
      movementType: 'warehouse-to-customer',
      durum: 'tedarik-bekleniyor',
      aracTipi: 'Filo',
      aracTuru: '',
      sevkKodu: `SEV-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
      siparisKodu: '',
      faturaBasligi: '',
      yuklemeTalepTarihi: '',
      yuklemeMusterisiAd: '',
      yuklemeIl: '',
      yuklemeIlce: '',
      teslimMusterisiAd: '',
      teslimIl: '',
      teslimIlce: '',
      stopCount: 1,
      vehicleCount: 1,
      palletCount: 0,
      yukAgirlik: 0,
      surucuBilgileri: '',
      yukTipi: '',
      aciklama: '',
      notlar: '',
      navlun: 0,
      maliyet: 0,
      talepOlusturan: '',
      shipmentContactPhone: '',
      islemDurum: 'islem-bekliyor',
      ozelYukBelgesi: 'Standart Yük',
      irsaliyeFaturaNotu: '',
      onOnayBelgesiNotu: '',
      faturaNumarasi: '',
      faturaNotlari: '',
      vkn: '',
      fiyatAciklama: '',
      eskalasyonNotu: '',
      tedarikciFirmaUnvani: '',
      muhasebeIletilmeTarihi: '',
      teslimBelgesiYuklemeTarihi: '',
      aracAtamasiGerceklestiren: '',
      requireReferenceWaybill: false,
      phoneOnlyQuote: false,
    });
  }, [editingItem, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const loadUnitStopFee = async () => {
      try {
        const systemSettings = await settingsService.getSettingValue('settings_system_preferences', { extraStopFee: 0 } as any);
        setUnitStopFee(Number((systemSettings as any)?.extraStopFee || 0));
      } catch (error) {
        console.error('Failed to load unit stop fee setting:', error);
        setUnitStopFee(0);
      }
    };

    void loadUnitStopFee();

    const loadDrivers = async () => {
      try {
        const [internalResponse, externalResponse] = await Promise.all([
          driverService.getDrivers({ type: 'internal', status: 'active' }),
          driverService.getDrivers({ type: 'external', status: 'active' }),
        ]);
        const items = [
          ...(Array.isArray((internalResponse as any)?.data) ? (internalResponse as any).data : []),
          ...(Array.isArray((externalResponse as any)?.data) ? (externalResponse as any).data : []),
        ];
        setDriverOptions(items);
      } catch (error) {
        console.error('Failed to load drivers for picker:', error);
      }
    };

    void loadDrivers();

    const loadMetadata = async () => {
      try {
        const response: any = await uiMetadataService.getSupplyForm();
        const tabs = Array.isArray(response?.data?.tabs) ? response.data.tabs : [];
        setMetadataTabs(tabs.map((tab: any) => ({ key: tab.key, label: tab.label, fields: Array.isArray(tab.fields) ? tab.fields : [] })));
      } catch (error) {
        console.error('Failed to load supply form metadata:', error);
      }
    };

    void loadMetadata();
  }, [isOpen]);

  useEffect(() => {
    const handler = async () => {
      if (!isOpen) return;
      try {
        const response: any = await uiMetadataService.getSupplyForm();
        const tabs = Array.isArray(response?.data?.tabs) ? response.data.tabs : [];
        setMetadataTabs(tabs.map((tab: any) => ({ key: tab.key, label: tab.label, fields: Array.isArray(tab.fields) ? tab.fields : [] })));
      } catch (error) {
        console.error('Failed to refresh supply form metadata:', error);
      }
    };

    window.addEventListener(SUPPLY_METADATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SUPPLY_METADATA_UPDATED_EVENT, handler);
  }, [isOpen]);

  useEffect(() => {
    const stopCount = Number(formData.stopCount || 0);
    const nextStopFee = Number((stopCount * unitStopFee).toFixed(2));
    setFormData((current: any) => {
      if (Number(current.ugarmaBedeli || 0) === nextStopFee) {
        return current;
      }
      return {
        ...current,
        ugarmaBedeli: nextStopFee,
      };
    });
  }, [formData.stopCount, unitStopFee]);

  const selectedStatus = useMemo(
    () => STATUS_OPTIONS.find((item) => item.value === formData.durum) || STATUS_OPTIONS[0],
    [formData.durum]
  );

  const calculatedTlKm = useMemo(() => {
    const seferKm = Number(formData.seferKm || 0);
    const navlun = Number(formData.navlun ?? formData.maliyet ?? 0);
    if (!seferKm) return Number(formData.gerceklesenTlKm || 0);
    return Number((navlun / seferKm).toFixed(2));
  }, [formData.gerceklesenTlKm, formData.maliyet, formData.navlun, formData.seferKm]);

  const calculatedGeneralTotal = useMemo(() => {
    const navlun = Number(formData.navlun ?? formData.maliyet ?? 0);
    const komisyon = Number(formData.komisyon || 0);
    const ugrama = Number(formData.ugarmaBedeli || 0);
    const bekleme = Number(formData.beklemeBedeli || 0);
    const hammaliye = Number(formData.hammaliyeBedeli || 0);
    const yatma = Number(formData.yatmaBedeli || 0);
    const explicit = Number(formData.genelToplam || 0);
    return explicit || navlun + komisyon + ugrama + bekleme + hammaliye + yatma;
  }, [formData.beklemeBedeli, formData.genelToplam, formData.hammaliyeBedeli, formData.komisyon, formData.maliyet, formData.navlun, formData.ugarmaBedeli, formData.yatmaBedeli]);

  const assignmentDriverName = formData.surucuAd || formData.surucuBilgileri || '';
  const assignmentPhone = formData.telefon || '';
  const selectedProcessStatus = PROCESS_STATUS_OPTIONS.find((item) => item.value === formData.islemDurum) || PROCESS_STATUS_OPTIONS[0];
  const irsaliyeDocuments = Array.isArray(formData.irsaliyeGorselleri) ? formData.irsaliyeGorselleri : [];
  const faturaDocuments = Array.isArray(formData.faturaGorselleri) ? formData.faturaGorselleri : [];
  const yuklemeBelge1Docs = Array.isArray(formData.yuklemeBelge1Docs) ? formData.yuklemeBelge1Docs : [];
  const yuklemeBelge2Docs = Array.isArray(formData.yuklemeBelge2Docs) ? formData.yuklemeBelge2Docs : [];
  const yuklemeBelge3Docs = Array.isArray(formData.yuklemeBelge3Docs) ? formData.yuklemeBelge3Docs : [];
  const bosaltmaBelge1Docs = Array.isArray(formData.bosaltmaBelge1Docs) ? formData.bosaltmaBelge1Docs : [];
  const bosaltmaBelge2Docs = Array.isArray(formData.bosaltmaBelge2Docs) ? formData.bosaltmaBelge2Docs : [];
  const bosaltmaBelge3Docs = Array.isArray(formData.bosaltmaBelge3Docs) ? formData.bosaltmaBelge3Docs : [];
  const vehicleTypeArray = useMemo(() => parseVehicleTypesFromForm(formData.aracTuru), [formData.aracTuru]);

  const activityEntries = useMemo(() => {
    const actor = formData.aracAtamasiGerceklestiren || formData.talepOlusturan || 'Sistem';
    const timeLabel = formData.updatedAt ? new Date(formData.updatedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '16:28';
    const entries = [
      { icon: <Link2 className="h-4 w-4" />, label: 'FATURA BAŞLIĞI (MÜŞTERİ)', value: formData.faturaBasligi, color: 'bg-slate-100 text-slate-700' },
      { icon: <Link2 className="h-4 w-4" />, label: 'BOŞALTMA İLİ', value: formData.teslimIl, color: 'bg-slate-100 text-slate-700' },
      { icon: <Hash className="h-4 w-4" />, label: 'UĞRAMA SAYISI', value: formData.stopCount, color: 'bg-slate-100 text-slate-700' },
      { icon: <List className="h-4 w-4" />, label: 'YÜKLENECEK VARDİYA', value: formData.operasyonTakipYuklenecekVardiya, color: 'bg-sky-100 text-sky-700' },
      { icon: <Link2 className="h-4 w-4" />, label: 'YÜKLEME İLÇESİ', value: formData.yuklemeIlce, color: 'bg-slate-100 text-slate-700' },
      { icon: <Package2 className="h-4 w-4" />, label: 'TONAJ', value: formData.yukAgirlik, color: 'bg-slate-100 text-slate-700' },
      { icon: <Link2 className="h-4 w-4" />, label: 'SÜRÜCÜ BİLGİLERİ', value: formData.surucuBilgileri, color: 'bg-slate-100 text-slate-700' },
      { icon: <CalendarDays className="h-4 w-4" />, label: 'ARAÇ ATAMA TARİHİ', value: formData.aracAtamaTarihi ? toDateTimeLocal(formData.aracAtamaTarihi) : '', color: 'bg-slate-100 text-slate-700' },
      { icon: <Link2 className="h-4 w-4" />, label: 'YÜKLEME İLİ', value: formData.yuklemeIl, color: 'bg-slate-100 text-slate-700' },
      { icon: <List className="h-4 w-4" />, label: 'BOŞALTMA MÜŞTERİSİ', value: formData.teslimMusterisiAd, color: 'bg-slate-100 text-slate-700' },
      { icon: <Link2 className="h-4 w-4" />, label: 'YÜKLEME MÜŞTERİSİ', value: formData.yuklemeMusterisiAd, color: 'bg-slate-100 text-slate-700' },
      { icon: <CalendarDays className="h-4 w-4" />, label: 'YÜKLEME TALEP TARİHİ', value: formData.yuklemeTalepTarihi ? toDateTimeLocal(formData.yuklemeTalepTarihi) : '', color: 'bg-slate-100 text-slate-700' },
      { icon: <FileText className="h-4 w-4" />, label: 'ÖZEL YÜK BELGESİ', value: formData.ozelYukBelgesi, color: 'bg-slate-100 text-slate-700' },
      { icon: <ClipboardList className="h-4 w-4" />, label: 'DURUM', value: selectedStatus.label, color: selectedStatus.className },
      { icon: <Link2 className="h-4 w-4" />, label: 'BOŞALTMA İLÇESİ', value: formData.teslimIlce, color: 'bg-slate-100 text-slate-700' },
      { icon: <ClipboardList className="h-4 w-4" />, label: 'İŞLEM DURUM', value: selectedProcessStatus.label, color: selectedProcessStatus.className },
      { icon: <Truck className="h-4 w-4" />, label: 'ARAÇ TÜRÜ', value: formData.aracTuru, color: 'bg-slate-100 text-slate-700' },
      { icon: <WalletCards className="h-4 w-4" />, label: 'NAVLUN', value: formData.navlun ?? formData.maliyet, color: 'bg-slate-100 text-slate-700' },
      { icon: <Hash className="h-4 w-4" />, label: 'SEFER KM', value: formData.seferKm ?? formData.mesafe, color: 'bg-slate-100 text-slate-700' },
      { icon: <Hash className="h-4 w-4" />, label: 'PALET SAYISI', value: formData.palletCount, color: 'bg-slate-100 text-slate-700' },
      { icon: <List className="h-4 w-4" />, label: 'HİZMET TÜRÜ', value: formData.yukTipi, color: 'bg-pink-100 text-pink-700' },
      { icon: <Link2 className="h-4 w-4" />, label: 'OPERASYON TAKİP (SEVK KODU)', value: formData.sevkKodu, color: 'bg-slate-100 text-slate-700' },
    ];

    return entries.filter((entry) => String(entry.value ?? '').trim() !== '').map((entry, index) => ({
      ...entry,
      id: `${entry.label}-${index}`,
      actor,
      timeLabel,
    }));
  }, [formData, selectedProcessStatus.className, selectedProcessStatus.label, selectedStatus.className, selectedStatus.label]);

  const commentPreview = {
    initials: String(formData.aracAtamasiGerceklestiren || formData.talepOlusturan || 'DO').split(' ').filter(Boolean).slice(0, 2).map((part: string) => part[0]).join('').toUpperCase() || 'DO',
    name: formData.aracAtamasiGerceklestiren || formData.talepOlusturan || 'DrMetin Özşahin',
    relativeTime: 'Bir dakikadan az bir süre önce',
    message: formData.fiyatAciklama || formData.aciklama || 'Deneme',
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleSaveClick = async () => {
    try {
      setSaving(true);
      await onSave({
        ...formData,
        istenenTeslimTarihi: formData.istenenTeslimTarihi || null,
        assignedVehicleId: formData.assignedVehicleId || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (docType: 'irsaliye' | 'fatura' | 'yuklemeBelge1' | 'yuklemeBelge2' | 'yuklemeBelge3' | 'bosaltmaBelge1' | 'bosaltmaBelge2' | 'bosaltmaBelge3', files: FileList | null) => {
    if (!editingItem?.id || !files || files.length === 0) return;

    try {
      setUploadingDocType(docType);
      const uploaded = [] as Array<{ id: string; url: string; name: string }>;

      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('file', file);
        form.append('entityType', 'shipment');
        form.append('entityId', String(editingItem.id));
        
        const docTypeMap: Record<string, string> = {
          irsaliye: 'irsaliye-gorseli',
          fatura: 'fatura-gorseli',
          yuklemeBelge1: 'yukleme-belge-1',
          yuklemeBelge2: 'yukleme-belge-2',
          yuklemeBelge3: 'yukleme-belge-3',
          bosaltmaBelge1: 'bosaltma-belge-1',
          bosaltmaBelge2: 'bosaltma-belge-2',
          bosaltmaBelge3: 'bosaltma-belge-3',
        };
        form.append('documentType', docTypeMap[docType] || docType);
        form.append('name', file.name);
        
        const response: any = await documentService.uploadDocument(form);
        uploaded.push({
          id: String(response?.data?.id || `${docType}-${file.name}-${Date.now()}`),
          url: response?.data?.filePath ? `${(viteEnv.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${response.data.filePath}` : URL.createObjectURL(file),
          name: response?.data?.fileName || file.name,
        });
      }

      const fieldMap: Record<string, string> = {
        irsaliye: 'irsaliyeGorselleri',
        fatura: 'faturaGorselleri',
        yuklemeBelge1: 'yuklemeBelge1Docs',
        yuklemeBelge2: 'yuklemeBelge2Docs',
        yuklemeBelge3: 'yuklemeBelge3Docs',
        bosaltmaBelge1: 'bosaltmaBelge1Docs',
        bosaltmaBelge2: 'bosaltmaBelge2Docs',
        bosaltmaBelge3: 'bosaltmaBelge3Docs',
      };

      setFormData((current: any) => ({
        ...current,
        [fieldMap[docType]]: [
          ...(Array.isArray(current[fieldMap[docType]]) ? current[fieldMap[docType]] : []),
          ...uploaded,
        ],
      }));
    } catch (error) {
      console.error('Document upload failed:', error);
    } finally {
      setUploadingDocType(null);
    }
  };

  const filteredDriverOptions = useMemo(() => {
    const q = driverSearch.trim().toLocaleLowerCase('tr-TR');
    if (!q) return driverOptions;
    return driverOptions.filter((item) => {
      const haystack = [item.full_name, item.phone, item.cekici_plaka, item.dorse_plaka, item.tc_number]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('tr-TR');
      return haystack.includes(q);
    });
  }, [driverOptions, driverSearch]);

  const handleSelectDriver = (driver: any) => {
    setFormData((current: any) => ({
      ...current,
      surucuBilgileri: driver.full_name || '',
      surucuAd: driver.full_name || '',
      telefon: driver.phone || current.telefon,
      tcKimlikNo: driver.tc_number || current.tcKimlikNo,
      cekiciPlaka: driver.cekici_plaka || current.cekiciPlaka,
      dorsePlaka: driver.dorse_plaka || current.dorsePlaka,
      faturaBasligi: current.faturaBasligi || driver.invoice_title || '',
    }));
    setDriverPickerOpen(false);
    setDriverSearch('');
  };

  const getFieldMeta = (fieldKey: string) => {
    for (const tab of resolvedTabs) {
      const found = (tab.fields || []).find((field: any) => field.key === fieldKey);
      if (found) return found;
    }
    return null;
  };

  const openLinkedPicker = async (fieldKey: string) => {
    const fieldMeta: any = getFieldMeta(fieldKey);
    const moduleKey = fieldMeta?.settings?.dataSource?.moduleKey;
    if (!moduleKey) return;
    setLinkedPicker({ fieldKey, moduleKey, open: true });
    setLinkedSearch('');
    try {
      const response: any = await uiMetadataService.searchLinkedRecords(moduleKey, '');
      setLinkedResults(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Linked picker load failed:', error);
      setLinkedResults([]);
    }
  };

  const searchLinkedResults = async (value: string) => {
    setLinkedSearch(value);
    if (!linkedPicker.moduleKey) return;
    try {
      const response: any = await uiMetadataService.searchLinkedRecords(linkedPicker.moduleKey, value);
      setLinkedResults(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Linked record search failed:', error);
    }
  };

  const applyLinkedSelection = (item: any) => {
    const fieldKey = linkedPicker.fieldKey;
    const fieldMeta: any = getFieldMeta(fieldKey);
    const fillMapping = fieldMeta?.settings?.dataSource?.fillMapping || null;
    setFormData((current: any) => {
      const next = { ...current };
      if (fillMapping && Object.keys(fillMapping).length > 0) {
        for (const [targetKey, sourceKey] of Object.entries(fillMapping)) {
          next[targetKey] = item.columns?.[String(sourceKey)] ?? item[String(sourceKey)] ?? item.value ?? next[targetKey];
        }
      } else {
        next[fieldKey] = item.value;
      }
      return next;
    });
    setLinkedPicker({ fieldKey: '', moduleKey: '', open: false });
    setLinkedSearch('');
    setLinkedResults([]);
  };

  const renderFieldByKey = (fieldKey: string) => {
    switch (fieldKey) {
      case 'sevkKodu':
        return <WorkiomFieldRow icon={<Hash className="h-4 w-4" />} label="Sevk Kodu"><WorkiomInput value={formData.sevkKodu || ''} onChange={(e) => setFormData({ ...formData, sevkKodu: e.target.value })} placeholder="Metin Ekle" /></WorkiomFieldRow>;
      case 'siparisKodu':
        return <WorkiomFieldRow icon={<Hash className="h-4 w-4" />} label="Sipariş Kodu"><WorkiomInput value={formData.siparisKodu || ''} onChange={(e) => setFormData({ ...formData, siparisKodu: e.target.value })} placeholder="Metin Ekle" /></WorkiomFieldRow>;
      case 'faturaBasligi':
        return <WorkiomFieldRow icon={<Link2 className="h-4 w-4" />} label="Fatura Başlığı (Müşteri)" required><div><button type="button" onClick={() => openLinkedPicker('faturaBasligi')} className="h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-left text-[15px] text-slate-800 hover:border-blue-400">{formData.faturaBasligi || 'Bağlantılı liste kaydı bul ve ekle'}</button><WorkiomLinkedPreview columns={[{ label: 'Ad', value: formData.faturaBasligi }]} /></div></WorkiomFieldRow>;
      case 'yuklemeTalepTarihi':
        return <WorkiomFieldRow icon={<CalendarDays className="h-4 w-4" />} label="Yükleme Talep Tarihi" required><WorkiomInput type="datetime-local" value={formData.yuklemeTalepTarihi || ''} onChange={(e) => setFormData({ ...formData, yuklemeTalepTarihi: e.target.value })} /></WorkiomFieldRow>;
      case 'yuklemeMusterisiAd':
        return <WorkiomFieldRow icon={<Link2 className="h-4 w-4" />} label="Yükleme Müşterisi" required><div><button type="button" onClick={() => openLinkedPicker('yuklemeMusterisiAd')} className="h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-left text-[15px] text-slate-800 hover:border-blue-400">{formData.yuklemeMusterisiAd || 'Bağlantılı liste kaydı bul ve ekle'}</button><WorkiomLinkedPreview columns={[{ label: 'Müşteri Adı', value: formData.yuklemeMusterisiAd }, { label: 'İl', value: formData.yuklemeIl }, { label: 'İlçe', value: formData.yuklemeIlce }, { label: 'Adres', value: formData.yuklemeAdres }]} /></div></WorkiomFieldRow>;
      case 'yuklemeIl':
        return <WorkiomFieldRow icon={<Link2 className="h-4 w-4" />} label="Yükleme İli" required><div><WorkiomInput value={formData.yuklemeIl || ''} onChange={(e) => setFormData({ ...formData, yuklemeIl: e.target.value })} placeholder="Bağlantılı liste kaydı bul ve ekle" /><WorkiomLinkedPreview columns={[{ label: 'İl', value: formData.yuklemeIl }]} /></div></WorkiomFieldRow>;
      case 'yuklemeIlce':
        return <WorkiomFieldRow icon={<Link2 className="h-4 w-4" />} label="Yükleme İlçesi" required><div><WorkiomInput value={formData.yuklemeIlce || ''} onChange={(e) => setFormData({ ...formData, yuklemeIlce: e.target.value })} placeholder="Bağlantılı liste kaydı bul ve ekle" /><WorkiomLinkedPreview columns={[{ label: 'Ad', value: formData.yuklemeIlce }]} /></div></WorkiomFieldRow>;
      case 'teslimMusterisiAd':
        return <WorkiomFieldRow icon={<List className="h-4 w-4" />} label="Boşaltma Müşterisi" required><WorkiomInput value={formData.teslimMusterisiAd || ''} onChange={(e) => setFormData({ ...formData, teslimMusterisiAd: e.target.value })} placeholder="Metin Ekle" /></WorkiomFieldRow>;
      case 'teslimIl':
        return <WorkiomFieldRow icon={<Link2 className="h-4 w-4" />} label="Boşaltma İli" required><div><WorkiomInput value={formData.teslimIl || ''} onChange={(e) => setFormData({ ...formData, teslimIl: e.target.value })} placeholder="Bağlantılı liste kaydı bul ve ekle" /><WorkiomLinkedPreview columns={[{ label: 'İl', value: formData.teslimIl }]} /></div></WorkiomFieldRow>;
      case 'teslimIlce':
        return <WorkiomFieldRow icon={<Link2 className="h-4 w-4" />} label="Boşaltma İlçesi" required><div><WorkiomInput value={formData.teslimIlce || ''} onChange={(e) => setFormData({ ...formData, teslimIlce: e.target.value })} placeholder="Bağlantılı liste kaydı bul ve ekle" /><WorkiomLinkedPreview columns={[{ label: 'Ad', value: formData.teslimIlce }]} /></div></WorkiomFieldRow>;
      case 'stopCount':
        return <WorkiomFieldRow icon={<Hash className="h-4 w-4" />} label="Uğrama Sayısı" required><WorkiomInput type="number" value={formData.stopCount ?? 1} onChange={(e) => setFormData({ ...formData, stopCount: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'vehicleCount':
        return <WorkiomFieldRow icon={<Hash className="h-4 w-4" />} label="Araç Sayısı" required><WorkiomInput type="number" value={formData.vehicleCount ?? 1} onChange={(e) => setFormData({ ...formData, vehicleCount: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'palletCount':
        return <WorkiomFieldRow icon={<Hash className="h-4 w-4" />} label="Palet Sayısı" required><WorkiomInput type="number" value={formData.palletCount ?? 0} onChange={(e) => setFormData({ ...formData, palletCount: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'yukAgirlik':
        return <WorkiomFieldRow icon={<Package2 className="h-4 w-4" />} label="Tonaj" required><WorkiomInput type="number" value={formData.yukAgirlik ?? 0} onChange={(e) => setFormData({ ...formData, yukAgirlik: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'aracTuru':
        return <WorkiomFieldRow icon={<Truck className="h-4 w-4" />} label="Araç Türü (birden çok seçilebilir)"><MultiVehicleTypeSelect value={vehicleTypeArray} onChange={(v) => setFormData({ ...formData, aracTuru: formatVehicleTypes(v) })} /></WorkiomFieldRow>;
      case 'surucuBilgileri':
        return <WorkiomFieldRow icon={<UserCircle2 className="h-4 w-4" />} label="Sürücü Bilgileri"><div><button type="button" onClick={() => setDriverPickerOpen(true)} className="h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-left text-[15px] text-slate-800 hover:border-blue-400">{formData.surucuBilgileri || 'Bağlantılı liste kaydı bul ve ekle'}</button>{activeTab === 'assignment' && <WorkiomLinkedPreview columns={[{ label: 'Hızlı Kayıt', value: formData.surucuBilgileri }, { label: 'Çekici', value: formData.cekici || formData.cekiciPlaka }, { label: 'Dorse', value: formData.dorsePlaka }, { label: 'Tip', value: formData.aracTipi }]} />}</div></WorkiomFieldRow>;
      case 'durum':
        return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Durum" required><div className="relative inline-block"><select value={formData.durum || 'tedarik-bekleniyor'} onChange={(e) => setFormData({ ...formData, durum: e.target.value })} className={`min-w-[240px] appearance-none rounded-xl px-5 py-3 pr-10 text-[15px] font-semibold focus:outline-none ${selectedStatus.className}`}>{STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select><ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white" /></div></WorkiomFieldRow>;
      case 'yukTipi':
        return <WorkiomFieldRow icon={<List className="h-4 w-4" />} label="Hizmet Türü"><div><WorkiomInput value={formData.yukTipi || ''} onChange={(e) => setFormData({ ...formData, yukTipi: e.target.value })} placeholder="Statik Liste Değeri Ekle" />{activeTab === 'pricing' && <WorkiomLinkedPreview columns={[{ label: 'Hizmet Türü', value: formData.yukTipi }]} />}</div></WorkiomFieldRow>;
      case 'aciklama':
      case 'fiyatAciklama':
        return <WorkiomFieldRow icon={<AlignLeft className="h-4 w-4" />} label="Açıklama"><WorkiomTextArea rows={3} value={formData.fiyatAciklama || formData.aciklama || formData.notlar || ''} onChange={(e) => setFormData({ ...formData, fiyatAciklama: e.target.value, aciklama: e.target.value, notlar: e.target.value })} placeholder="Metin Ekle" /></WorkiomFieldRow>;
      case 'navlun':
        return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Navlun"><WorkiomInput type="number" value={formData.navlun ?? formData.maliyet ?? 0} onChange={(e) => setFormData({ ...formData, navlun: Number(e.target.value), maliyet: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'talepOlusturan':
        return <WorkiomFieldRow icon={<Link2 className="h-4 w-4" />} label="Talep Oluşturan"><WorkiomInput value={formData.talepOlusturan || ''} onChange={(e) => setFormData({ ...formData, talepOlusturan: e.target.value })} placeholder="Bağlantılı liste kaydı bul ve ekle" /></WorkiomFieldRow>;
      case 'operasyonTakipYuklenecekVardiya': return <WorkiomFieldRow icon={<List className="h-4 w-4" />} label="Yüklenecek Vardiya"><WorkiomInput value={formData.operasyonTakipYuklenecekVardiya || ''} onChange={(e) => setFormData({ ...formData, operasyonTakipYuklenecekVardiya: e.target.value })} placeholder="Vardiya Ekle" /></WorkiomFieldRow>;
      case 'aracAtamaTarihi': return <WorkiomFieldRow icon={<CalendarDays className="h-4 w-4" />} label="Araç Atama Tarihi"><WorkiomInput type="datetime-local" value={toDateTimeLocal(formData.aracAtamaTarihi)} onChange={(e) => setFormData({ ...formData, aracAtamaTarihi: e.target.value })} /></WorkiomFieldRow>;
      case 'surucuAd': return <WorkiomFieldRow icon={<UserCircle2 className="h-4 w-4" />} label="Sürücü Adı"><WorkiomInput value={assignmentDriverName} readOnly className="bg-slate-100 text-slate-500" /></WorkiomFieldRow>;
      case 'tcKimlikNo': return <WorkiomFieldRow icon={<Hash className="h-4 w-4" />} label="TC Kimlik No"><WorkiomInput value={formData.tcKimlikNo || ''} readOnly className="bg-slate-100 text-slate-500" /></WorkiomFieldRow>;
      case 'telefon': return <WorkiomFieldRow icon={<Link2 className="h-4 w-4" />} label="Telefon"><div className="flex items-center gap-2"><WorkiomInput value={assignmentPhone} onChange={(e) => setFormData({ ...formData, telefon: e.target.value })} placeholder="+9050012345678" className={assignmentPhone ? '' : 'bg-slate-100'} />{assignmentPhone && <><a href={`tel:${assignmentPhone}`} className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-50"><Phone className="h-4 w-4" /></a><a href={`https://wa.me/${assignmentPhone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-300 p-2 text-slate-600 hover:bg-slate-50"><MessageCircle className="h-4 w-4" /></a></>}</div></WorkiomFieldRow>;
      case 'seferKm': return <WorkiomFieldRow icon={<Hash className="h-4 w-4" />} label="Sefer KM"><WorkiomInput type="number" value={formData.seferKm ?? formData.mesafe ?? 0} onChange={(e) => setFormData({ ...formData, seferKm: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'komisyon': return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Komisyon"><WorkiomInput type="number" value={formData.komisyon ?? 0} onChange={(e) => setFormData({ ...formData, komisyon: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'ugarmaBedeli': return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Durak Bedeli"><WorkiomInput type="number" value={formData.ugarmaBedeli ?? 0} readOnly className="bg-slate-100 text-slate-700" /></WorkiomFieldRow>;
      case 'beklemeBedeli': return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Bekleme Bedeli"><WorkiomInput type="number" value={formData.beklemeBedeli ?? 0} onChange={(e) => setFormData({ ...formData, beklemeBedeli: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'hammaliyeBedeli': return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Hammaliye Bedeli"><WorkiomInput type="number" value={formData.hammaliyeBedeli ?? 0} onChange={(e) => setFormData({ ...formData, hammaliyeBedeli: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'yatmaBedeli': return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Yatma Bedeli"><WorkiomInput type="number" value={formData.yatmaBedeli ?? 0} onChange={(e) => setFormData({ ...formData, yatmaBedeli: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'gerceklesenTlKm': return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Gerçekleşen TL/KM"><WorkiomInput value={String(calculatedTlKm || '')} readOnly className="bg-slate-100 text-slate-700" /></WorkiomFieldRow>;
      case 'genelToplam': return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Genel Toplam"><WorkiomInput value={String(calculatedGeneralTotal || '')} readOnly className="bg-slate-100 text-slate-700" /></WorkiomFieldRow>;
      case 'ozelYukBelgesi': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Özel Yük Belgesi"><WorkiomInput value={formData.ozelYukBelgesi || ''} onChange={(e) => setFormData({ ...formData, ozelYukBelgesi: e.target.value })} /></WorkiomFieldRow>;
      case 'irsaliyeFaturaNotu': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="İrsaliye & Fatura Alanı"><div><div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5"><input value={formData.irsaliyeFaturaNotu || ''} onChange={(e) => setFormData({ ...formData, irsaliyeFaturaNotu: e.target.value })} placeholder="Alan seçiniz ve alanları yapıştırınız veya sürükleyiniz" className="flex-1 text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none" /><button type="button" className="text-sm font-semibold text-blue-600">+ Yükle</button></div><DocumentUploadPanel documents={irsaliyeDocuments} loading={uploadingDocType === 'irsaliye'} open={openDocumentPopover === 'irsaliye'} onToggle={() => setOpenDocumentPopover((current) => current === 'irsaliye' ? null : 'irsaliye')} title="İrsaliye görseli" onUpload={(files) => void handleDocumentUpload('irsaliye', files)} onPreview={(index) => setDocumentPreviewModal({ title: 'İrsaliye Görselleri', documents: irsaliyeDocuments, index })} />{activeTab !== 'pricing' && <div className="mt-4"><div className="mb-2 text-sm font-semibold text-slate-700">Fatura Görselleri</div><DocumentUploadPanel documents={faturaDocuments} loading={uploadingDocType === 'fatura'} open={openDocumentPopover === 'fatura'} onToggle={() => setOpenDocumentPopover((current) => current === 'fatura' ? null : 'fatura')} title="Fatura görseli" onUpload={(files) => void handleDocumentUpload('fatura', files)} onPreview={(index) => setDocumentPreviewModal({ title: 'Fatura Görselleri', documents: faturaDocuments, index })} /></div>}</div></WorkiomFieldRow>;
      case 'faturaNumarasi': return <WorkiomFieldRow icon={<Hash className="h-4 w-4" />} label="Fatura Numarası"><WorkiomInput value={formData.faturaNumarasi || ''} onChange={(e) => setFormData({ ...formData, faturaNumarasi: e.target.value })} placeholder="Metin Ekle" /></WorkiomFieldRow>;
      case 'islemDurum': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="İşlem Durum" required><div className="relative inline-block"><select value={formData.islemDurum || 'islem-bekliyor'} onChange={(e) => setFormData({ ...formData, islemDurum: e.target.value })} className={`min-w-[240px] appearance-none rounded-xl px-5 py-3 pr-10 text-[15px] font-semibold focus:outline-none ${selectedProcessStatus.className}`}>{PROCESS_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select><ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white" /></div></WorkiomFieldRow>;
      case 'eskalasyonNotu': return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Eskalasyon Notu"><WorkiomInput value={formData.eskalasyonNotu || ''} readOnly className="bg-slate-100 text-slate-500" /></WorkiomFieldRow>;
      case 'faturaNotlari': return <WorkiomFieldRow icon={<AlignLeft className="h-4 w-4" />} label="Fatura Notları"><WorkiomInput value={formData.faturaNotlari || ''} onChange={(e) => setFormData({ ...formData, faturaNotlari: e.target.value })} placeholder="Metin Ekle" /></WorkiomFieldRow>;
      case 'vkn': return <WorkiomFieldRow icon={<Hash className="h-4 w-4" />} label="VKN"><WorkiomInput value={formData.vkn || ''} onChange={(e) => setFormData({ ...formData, vkn: e.target.value })} placeholder="Metin Ekle" /></WorkiomFieldRow>;
      case 'faturaTutari': return <WorkiomFieldRow icon={<WalletCards className="h-4 w-4" />} label="Fatura Tutarı"><WorkiomInput type="number" value={formData.faturaTutari ?? 0} onChange={(e) => setFormData({ ...formData, faturaTutari: Number(e.target.value) })} /></WorkiomFieldRow>;
      case 'faturaTarihi': return <WorkiomFieldRow icon={<CalendarDays className="h-4 w-4" />} label="Fatura Tarihi"><WorkiomInput type="datetime-local" value={toDateTimeLocal(formData.faturaTarihi)} onChange={(e) => setFormData({ ...formData, faturaTarihi: e.target.value })} /></WorkiomFieldRow>;
      case 'onOnayBelgesiNotu': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Ön Onay Belgesi"><div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5"><input value={formData.onOnayBelgesiNotu || ''} onChange={(e) => setFormData({ ...formData, onOnayBelgesiNotu: e.target.value })} placeholder="Alan seçiniz ve alanları yapıştırınız veya sürükleyiniz" className="flex-1 text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none" /><button type="button" className="text-sm font-semibold text-blue-600">+ Yükle</button></div></WorkiomFieldRow>;
      case 'tedarikciFirmaUnvani': return <WorkiomFieldRow icon={<AlignLeft className="h-4 w-4" />} label="Tedarikçi Firma Ünvanı"><WorkiomInput value={formData.tedarikciFirmaUnvani || ''} onChange={(e) => setFormData({ ...formData, tedarikciFirmaUnvani: e.target.value })} placeholder="Metin Ekle" /></WorkiomFieldRow>;
      case 'muhasebeIletilmeTarihi': return <WorkiomFieldRow icon={<CalendarDays className="h-4 w-4" />} label="Muhasebe İletilme Tarihi"><WorkiomInput type="datetime-local" value={toDateTimeLocal(formData.muhasebeIletilmeTarihi)} onChange={(e) => setFormData({ ...formData, muhasebeIletilmeTarihi: e.target.value })} /></WorkiomFieldRow>;
      case 'teslimBelgesiYuklemeTarihi': return <WorkiomFieldRow icon={<CalendarDays className="h-4 w-4" />} label="Teslim Belgesi Yükleme Tarihi"><WorkiomInput type="datetime-local" value={toDateTimeLocal(formData.teslimBelgesiYuklemeTarihi)} onChange={(e) => setFormData({ ...formData, teslimBelgesiYuklemeTarihi: e.target.value })} /></WorkiomFieldRow>;
      case 'aracAtamasiGerceklestiren': return <WorkiomFieldRow icon={<UserCircle2 className="h-4 w-4" />} label="Araç Ataması Gerçekleştiren"><WorkiomInput value={formData.aracAtamasiGerceklestiren || ''} onChange={(e) => setFormData({ ...formData, aracAtamasiGerceklestiren: e.target.value })} placeholder="Kullanıcı Ekle" /></WorkiomFieldRow>;
      
      case 'yuklemeIrslaliyeNo': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Yükleme İrsaliye No"><WorkiomInput value={formData.yuklemeIrslaliyeNo || ''} onChange={(e) => setFormData({ ...formData, yuklemeIrslaliyeNo: e.target.value })} placeholder="İrsaliye numarası giriniz" /></WorkiomFieldRow>;
      case 'yuklemeTutanakNo': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Yükleme Tutanak No"><WorkiomInput value={formData.yuklemeTutanakNo || ''} onChange={(e) => setFormData({ ...formData, yuklemeTutanakNo: e.target.value })} placeholder="Tutanak numarası giriniz" /></WorkiomFieldRow>;
      case 'yuklemeBelge1': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Yükleme Belgesi 1"><div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5"><input value={formData.yuklemeBelge1 || ''} onChange={(e) => setFormData({ ...formData, yuklemeBelge1: e.target.value })} placeholder="Belge açıklaması veya numarası" className="flex-1 text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none" /></div><DocumentUploadPanel documents={yuklemeBelge1Docs} loading={uploadingDocType === 'yuklemeBelge1'} open={openDocumentPopover === 'yuklemeBelge1'} onToggle={() => setOpenDocumentPopover((current) => current === 'yuklemeBelge1' ? null : 'yuklemeBelge1')} title="Yükleme Belgesi 1" onUpload={(files) => void handleDocumentUpload('yuklemeBelge1', files)} onPreview={(index) => setDocumentPreviewModal({ title: 'Yükleme Belgesi 1', documents: yuklemeBelge1Docs, index })} /></WorkiomFieldRow>;
      case 'yuklemeBelge2': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Yükleme Belgesi 2"><div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5"><input value={formData.yuklemeBelge2 || ''} onChange={(e) => setFormData({ ...formData, yuklemeBelge2: e.target.value })} placeholder="Belge açıklaması veya numarası" className="flex-1 text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none" /></div><DocumentUploadPanel documents={yuklemeBelge2Docs} loading={uploadingDocType === 'yuklemeBelge2'} open={openDocumentPopover === 'yuklemeBelge2'} onToggle={() => setOpenDocumentPopover((current) => current === 'yuklemeBelge2' ? null : 'yuklemeBelge2')} title="Yükleme Belgesi 2" onUpload={(files) => void handleDocumentUpload('yuklemeBelge2', files)} onPreview={(index) => setDocumentPreviewModal({ title: 'Yükleme Belgesi 2', documents: yuklemeBelge2Docs, index })} /></WorkiomFieldRow>;
      case 'yuklemeBelge3': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Yükleme Belgesi 3"><div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5"><input value={formData.yuklemeBelge3 || ''} onChange={(e) => setFormData({ ...formData, yuklemeBelge3: e.target.value })} placeholder="Belge açıklaması veya numarası" className="flex-1 text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none" /></div><DocumentUploadPanel documents={yuklemeBelge3Docs} loading={uploadingDocType === 'yuklemeBelge3'} open={openDocumentPopover === 'yuklemeBelge3'} onToggle={() => setOpenDocumentPopover((current) => current === 'yuklemeBelge3' ? null : 'yuklemeBelge3')} title="Yükleme Belgesi 3" onUpload={(files) => void handleDocumentUpload('yuklemeBelge3', files)} onPreview={(index) => setDocumentPreviewModal({ title: 'Yükleme Belgesi 3', documents: yuklemeBelge3Docs, index })} /></WorkiomFieldRow>;
      
      case 'bosaltmaTeslimBelgeNo': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Boşaltma Teslim Belge No"><WorkiomInput value={formData.bosaltmaTeslimBelgeNo || ''} onChange={(e) => setFormData({ ...formData, bosaltmaTeslimBelgeNo: e.target.value })} placeholder="Teslim belgesi numarası giriniz" /></WorkiomFieldRow>;
      case 'bosaltmaTutanakNo': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Boşaltma Tutanak No"><WorkiomInput value={formData.bosaltmaTutanakNo || ''} onChange={(e) => setFormData({ ...formData, bosaltmaTutanakNo: e.target.value })} placeholder="Tutanak numarası giriniz" /></WorkiomFieldRow>;
      case 'bosaltmaBelge1': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Boşaltma Belgesi 1"><div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5"><input value={formData.bosaltmaBelge1 || ''} onChange={(e) => setFormData({ ...formData, bosaltmaBelge1: e.target.value })} placeholder="Belge açıklaması veya numarası" className="flex-1 text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none" /></div><DocumentUploadPanel documents={bosaltmaBelge1Docs} loading={uploadingDocType === 'bosaltmaBelge1'} open={openDocumentPopover === 'bosaltmaBelge1'} onToggle={() => setOpenDocumentPopover((current) => current === 'bosaltmaBelge1' ? null : 'bosaltmaBelge1')} title="Boşaltma Belgesi 1" onUpload={(files) => void handleDocumentUpload('bosaltmaBelge1', files)} onPreview={(index) => setDocumentPreviewModal({ title: 'Boşaltma Belgesi 1', documents: bosaltmaBelge1Docs, index })} /></WorkiomFieldRow>;
      case 'bosaltmaBelge2': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Boşaltma Belgesi 2"><div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5"><input value={formData.bosaltmaBelge2 || ''} onChange={(e) => setFormData({ ...formData, bosaltmaBelge2: e.target.value })} placeholder="Belge açıklaması veya numarası" className="flex-1 text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none" /></div><DocumentUploadPanel documents={bosaltmaBelge2Docs} loading={uploadingDocType === 'bosaltmaBelge2'} open={openDocumentPopover === 'bosaltmaBelge2'} onToggle={() => setOpenDocumentPopover((current) => current === 'bosaltmaBelge2' ? null : 'bosaltmaBelge2')} title="Boşaltma Belgesi 2" onUpload={(files) => void handleDocumentUpload('bosaltmaBelge2', files)} onPreview={(index) => setDocumentPreviewModal({ title: 'Boşaltma Belgesi 2', documents: bosaltmaBelge2Docs, index })} /></WorkiomFieldRow>;
      case 'bosaltmaBelge3': return <WorkiomFieldRow icon={<FileText className="h-4 w-4" />} label="Boşaltma Belgesi 3"><div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5"><input value={formData.bosaltmaBelge3 || ''} onChange={(e) => setFormData({ ...formData, bosaltmaBelge3: e.target.value })} placeholder="Belge açıklaması veya numarası" className="flex-1 text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none" /></div><DocumentUploadPanel documents={bosaltmaBelge3Docs} loading={uploadingDocType === 'bosaltmaBelge3'} open={openDocumentPopover === 'bosaltmaBelge3'} onToggle={() => setOpenDocumentPopover((current) => current === 'bosaltmaBelge3' ? null : 'bosaltmaBelge3')} title="Boşaltma Belgesi 3" onUpload={(files) => void handleDocumentUpload('bosaltmaBelge3', files)} onPreview={(index) => setDocumentPreviewModal({ title: 'Boşaltma Belgesi 3', documents: bosaltmaBelge3Docs, index })} /></WorkiomFieldRow>;
      default:
        return null;
    }
  };

  const renderMetadataDrivenTab = (tabKey: string, title: string) => {
    const tab = resolvedTabs.find((item) => item.key === tabKey);
    const fieldKeys = (tab?.fields?.length ? tab.fields.map((field: any) => field.key) : (FALLBACK_TAB_FIELDS[tabKey] || [])).filter(Boolean);
    return (
      <div className="w-full rounded-2xl border border-slate-200 bg-white p-6">
        {title ? <div className="mb-6"><h3 className="text-[18px] font-semibold text-slate-900">{title}</h3></div> : null}
        <div className="space-y-6">
          {fieldKeys.map((fieldKey) => (
            <div key={`${tabKey}-${fieldKey}`}>{renderFieldByKey(fieldKey)}</div>
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const resolvedTabs = metadataTabs.length > 0 ? metadataTabs : FORM_TABS;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
      <div className="flex max-h-[94vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-2xl bg-[#f8fafc] shadow-2xl">
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-white px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:px-6 sm:py-5">
          <div className="min-w-0">
            <div className="text-sm text-slate-500">Operasyon Yönetimi &gt; Tedarik Yönetimi</div>
            <h2 className="mt-3 break-all text-[32px] font-bold leading-none text-slate-500 sm:text-[48px]">{formData.sevkKodu || 'SEVK KODU'}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-slate-200 bg-white px-4 sm:px-6">
            <div className="flex items-center gap-8 overflow-x-auto">
              {resolvedTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap border-b-2 px-1 py-4 text-[15px] font-semibold transition-colors ${activeTab === tab.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-700 hover:text-slate-900'}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {activeTab === 'general' && renderMetadataDrivenTab('general', 'Yük bilgileri')}
            {activeTab === 'assignment' && renderMetadataDrivenTab('assignment', 'Araç bilgileri')}
            {activeTab === 'pricing' && renderMetadataDrivenTab('pricing', 'Fiyat Giriş')}
            {activeTab === 'documents' && renderMetadataDrivenTab('documents', '')}

            {activeTab === 'activity' && (
              <div className="w-full rounded-2xl border border-slate-200 bg-white p-6">
                <div className="mb-6 text-[18px] font-semibold text-slate-900">Etkinlikler</div>
                <div className="flex items-center gap-6 border-b border-slate-200 pb-3">
                  <button type="button" onClick={() => setActivityTab('all')} className={`text-sm font-semibold ${activityTab === 'all' ? 'text-blue-600' : 'text-slate-700'}`}>Tüm Aktivite</button>
                  <button type="button" onClick={() => setActivityTab('comments')} className={`text-sm font-semibold ${activityTab === 'comments' ? 'text-blue-600' : 'text-slate-700'}`}>Yorumlar</button>
                  <button type="button" onClick={() => setActivityTab('history')} className={`text-sm font-semibold ${activityTab === 'history' ? 'text-blue-600' : 'text-slate-700'}`}>Değişiklik Kaydı</button>
                </div>

                <div className="mt-6 flex items-center justify-center">
                  <span className="rounded-full border border-slate-200 px-4 py-1 text-sm text-slate-500">Dün</span>
                </div>

                <div className="mt-6 space-y-5">
                  {(activityTab === 'comments' ? [] : activityEntries).map((entry) => (
                    <div key={entry.id} className="flex items-start gap-4 text-[15px] text-slate-700">
                      <div className="mt-1 text-slate-400">{entry.icon}</div>
                      <div className="flex-1 leading-6">
                        <span className="font-semibold text-slate-700">{entry.actor}</span>{' '}
                        <span className="text-slate-500">{entry.label} adlı alana</span>{' '}
                        <span className={`inline-flex rounded-md px-2 py-0.5 text-sm font-semibold ${entry.color}`}>{entry.value}</span>{' '}
                        <span className="text-slate-500">olarak değer atadı.</span>{' '}
                        <span className="text-slate-500">{entry.timeLabel}</span>
                      </div>
                    </div>
                  ))}

                  {activityTab === 'comments' && (
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-400 text-sm font-bold text-white">
                          {commentPreview.initials}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{commentPreview.name}</span>
                            <span className="text-sm text-slate-400">{commentPreview.relativeTime}</span>
                          </div>
                          <div className="mt-1 text-[15px] text-slate-800">{commentPreview.message}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-8 overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 text-slate-400">
                    <strong className="text-slate-300">B</strong>
                    <em className="text-slate-300">I</em>
                    <span className="text-slate-300">S</span>
                    <Link2 className="h-4 w-4" />
                    <List className="h-4 w-4" />
                    <MessageSquare className="h-4 w-4" />
                    <History className="h-4 w-4" />
                  </div>
                  <textarea className="min-h-[110px] w-full resize-none border-0 px-4 py-3 text-[15px] text-slate-700 placeholder:text-slate-400 focus:outline-none" placeholder="Yorum ekle" />
                  <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-slate-400">
                    <div className="flex items-center gap-3">
                      <Link2 className="h-4 w-4" />
                      <MessageCircle className="h-4 w-4" />
                      <span>@</span>
                    </div>
                    <button type="button" className="text-sm font-semibold text-slate-400">Paylaş</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700">İptal</button>
            <button type="button" onClick={handleSaveClick} disabled={saving} className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">
              <Save className="h-4 w-4" />
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>

      {documentPreviewModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <div className="text-base font-semibold text-slate-900">{documentPreviewModal.title}</div>
                <div className="text-xs text-slate-500">{documentPreviewModal.index + 1} / {documentPreviewModal.documents.length}</div>
              </div>
              <button type="button" onClick={() => setDocumentPreviewModal(null)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-5">
              <div className="flex min-h-[360px] items-center justify-center rounded-xl bg-slate-100">
                <img src={documentPreviewModal.documents[documentPreviewModal.index]?.url} alt={documentPreviewModal.documents[documentPreviewModal.index]?.name} className="max-h-[65vh] w-auto object-contain rounded-lg" />
              </div>
              <div className="mt-4 flex gap-3 overflow-x-auto">
                {documentPreviewModal.documents.map((doc, index) => (
                  <button key={doc.id} type="button" onClick={() => setDocumentPreviewModal((current) => current ? { ...current, index } : current)} className={`h-20 w-20 overflow-hidden rounded-lg border-2 ${index === documentPreviewModal.index ? 'border-blue-500' : 'border-transparent hover:border-slate-300'}`}>
                    <img src={doc.url} alt={doc.name} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {driverPickerOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-lg font-semibold text-slate-900">Sürücü Seç</div>
              <div className="mt-3">
                <input value={driverSearch} onChange={(e) => setDriverSearch(e.target.value)} placeholder="Ad, telefon, çekici, dorse, TC ara..." className="h-11 w-full rounded-lg border border-slate-200 px-4 text-[15px] focus:border-blue-400 focus:outline-none" />
              </div>
            </div>

            <div className="overflow-auto p-4">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Sürücü</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Telefon</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">TC Kimlik</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Çekici</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Dorse</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Tip</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDriverOptions.map((driver) => (
                    <tr key={driver.id} className="border-b border-slate-100">
                      <td className="px-3 py-3">{driver.full_name || '-'}</td>
                      <td className="px-3 py-3">{driver.phone || '-'}</td>
                      <td className="px-3 py-3">{driver.tc_number || '-'}</td>
                      <td className="px-3 py-3">{driver.cekici_plaka || '-'}</td>
                      <td className="px-3 py-3">{driver.dorse_plaka || '-'}</td>
                      <td className="px-3 py-3">{driver.driver_type === 'internal' ? 'Özmal' : 'Spot'}</td>
                      <td className="px-3 py-3">
                        <button type="button" onClick={() => handleSelectDriver(driver)} className="rounded-lg bg-blue-600 px-3 py-1.5 text-white hover:bg-blue-700">Seç</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end border-t border-slate-200 px-5 py-4">
              <button type="button" onClick={() => setDriverPickerOpen(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700">Kapat</button>
            </div>
          </div>
        </div>
      )}

      {linkedPicker.open && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-lg font-semibold text-slate-900">Bağlantılı Liste Kaydı Seç</div>
              <div className="mt-3">
                <input value={linkedSearch} onChange={(e) => searchLinkedResults(e.target.value)} placeholder="Yazdıkça filtrele..." className="h-11 w-full rounded-lg border border-slate-200 px-4 text-[15px] focus:border-blue-400 focus:outline-none" />
              </div>
            </div>
            <div className="overflow-auto p-4">
              <table className="w-full min-w-[900px] text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Ad</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Kod</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">İl</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">İlçe</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">Adres</th>
                    <th className="px-3 py-3 text-left font-semibold text-slate-700">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedResults.map((item) => (
                    <tr key={item.id} className="border-b border-slate-100">
                      <td className="px-3 py-3">{item.columns?.company_name || item.label}</td>
                      <td className="px-3 py-3">{item.columns?.code || '-'}</td>
                      <td className="px-3 py-3">{item.columns?.city || '-'}</td>
                      <td className="px-3 py-3">{item.columns?.district || '-'}</td>
                      <td className="px-3 py-3">{item.columns?.address || '-'}</td>
                      <td className="px-3 py-3"><button type="button" onClick={() => applyLinkedSelection(item)} className="rounded-lg bg-blue-600 px-3 py-1.5 text-white hover:bg-blue-700">Seç</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end border-t border-slate-200 px-5 py-4">
              <button type="button" onClick={() => setLinkedPicker({ fieldKey: '', moduleKey: '', open: false })} className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
